import { useState, useEffect, useCallback } from 'react';
import { ref, onValue, off, push, set, update, remove } from 'firebase/database';
import { database, app } from '../firebase';

export const useNotifications = (user) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // Cargar notificaciones del usuario
    useEffect(() => {
        if (!user?.uid) {
            setLoading(false);
            return;
        }

        const notificationsRef = ref(database, `notifications/${user.uid}`);

        const unsubscribe = onValue(notificationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const notificationsArray = Object.entries(data)
                    .map(([id, notification]) => ({
                        id,
                        ...notification,
                        fechaCreacion: new Date(notification.fechaCreacion)
                    }))
                    .sort((a, b) => b.fechaCreacion - a.fechaCreacion); // Más recientes primero

                setNotifications(notificationsArray);

                // Contar notificaciones no leídas
                const unread = notificationsArray.filter(n => !n.leida).length;
                setUnreadCount(unread);
            } else {
                setNotifications([]);
                setUnreadCount(0);
            }
            setLoading(false);
        });

        return () => {
            off(notificationsRef);
        };
    }, [user?.uid]);

    // Crear nueva notificación
    const createNotification = useCallback(async (notificationData) => {
        if (!user?.uid) return null;

        try {
            const notificationsRef = ref(database, `notifications/${user.uid}`);
            const newNotificationRef = push(notificationsRef);

            const notification = {
                ...notificationData,
                leida: false,
                fechaCreacion: new Date().toISOString(),
                id: newNotificationRef.key
            };

            await set(newNotificationRef, notification);
            return newNotificationRef.key;
        } catch (error) {
            console.error('Error creando notificación:', error);
            return null;
        }
    }, [user?.uid]);

    // Marcar notificación como leída
    const markAsRead = useCallback(async (notificationId) => {
        if (!user?.uid) return;

        try {
            const notificationRef = ref(database, `notifications/${user.uid}/${notificationId}`);
            await update(notificationRef, { leida: true });
        } catch (error) {
            console.error('Error marcando notificación como leída:', error);
        }
    }, [user?.uid]);

    // Marcar todas como leídas
    const markAllAsRead = useCallback(async () => {
        if (!user?.uid || notifications.length === 0) return;

        try {
            const updates = {};

            notifications.forEach(notification => {
                if (!notification.leida) {
                    updates[`notifications/usersId/${user.uid}/${notification.id}/leida`] = true;
                }
            });

            await update(ref(database), updates);
        } catch (error) {
            console.error('Error marcando todas las notificaciones como leídas:', error);
        }
    }, [user?.uid, notifications]);

    // Eliminar notificación
    const deleteNotification = useCallback(async (notificationId) => {
        if (!user?.uid) return;

        try {
            const notificationRef = ref(database, `notifications/${user.uid}/${notificationId}`);
            await remove(notificationRef);
        } catch (error) {
            console.error('Error eliminando notificación:', error);
        }
    }, [user?.uid]);

    // Limpiar todas las notificaciones
    const clearAllNotifications = useCallback(async () => {
        if (!user?.uid) return;

        try {
            const notificationsRef = ref(database, `notifications/${user.uid}`);
            await remove(notificationsRef);
        } catch (error) {
            console.error('Error limpiando notificaciones:', error);
        }
    }, [user?.uid]);

    // Funciones de conveniencia para crear tipos específicos de notificaciones
    const createStudyReminder = useCallback((planName, pendingTopics) => {
        return createNotification({
            tipo: 'estudio',
            titulo: 'Recordatorio de Estudio',
            mensaje: `Tienes ${pendingTopics} temas pendientes en tu plan "${planName}"`,
            icono: '📚',
            datosAdicionales: { planName, pendingTopics }
        });
    }, [createNotification]);

    const createProgressCelebration = useCallback((planName, progress) => {
        return createNotification({
            tipo: 'progreso',
            titulo: '¡Felicitaciones! 🎉',
            mensaje: `Has alcanzado el ${progress}% de progreso en tu plan "${planName}"`,
            icono: '🎯',
            datosAdicionales: { planName, progress }
        });
    }, [createNotification]);

    const createCommunityNotification = useCallback((message, postId = null) => {
        return createNotification({
            tipo: 'comunidad',
            titulo: 'Actividad en Comunidad',
            mensaje: message,
            icono: '👥',
            datosAdicionales: { postId }
        });
    }, [createNotification]);

    const createExamReminder = useCallback((examType, timeLeft) => {
        return createNotification({
            tipo: 'examen',
            titulo: 'Recordatorio de Examen',
            mensaje: `Tu examen de ${examType} comienza en ${timeLeft}`,
            icono: '⏰',
            datosAdicionales: { examType, timeLeft }
        });
    }, [createNotification]);

    const createSystemNotification = useCallback((title, message) => {
        return createNotification({
            tipo: 'sistema',
            titulo: title,
            mensaje: message,
            icono: 'ℹ️',
            datosAdicionales: {}
        });
    }, [createNotification]);

    return {
        notifications,
        unreadCount,
        loading,
        createNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        // Funciones de conveniencia
        createStudyReminder,
        createProgressCelebration,
        createCommunityNotification,
        createExamReminder,
        createSystemNotification
    };
};